import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

/** The latest platform audit row for a club's minimum code version. */
export interface MinAppCodeVersionAudit {
  actorUserId: string | null;
  createdAt: string;
  from: number | null;
}

interface Row {
  actor_user_id: string | null;
  created_at: string;
  metadata: { from?: unknown } | null;
}

const MIN_APP_CODE_VERSION_AUDIT_ACTION = "tenant.min_app_code_version.updated";

/**
 * KLUB-245 U3. Reads `platform_audit_log` directly: its only policy
 * (`platform_audit_klubora_full`) admits any active platform user via
 * `is_klubora_admin()`. Bump `reloadKey` after a save to refetch.
 * A read error leaves the footnote empty — it is context, not the value.
 */
export function useMinAppCodeVersionAudit(
  tenantId: string,
  reloadKey: number,
): MinAppCodeVersionAudit | null {
  const [audit, setAudit] = useState<MinAppCodeVersionAudit | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("platform_audit_log")
        .select("actor_user_id, created_at, metadata")
        .eq("target_tenant_id", tenantId)
        .eq("action", MIN_APP_CODE_VERSION_AUDIT_ACTION)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setAudit(null);
        return;
      }
      const row = data as Row;
      const from = row.metadata?.from;
      setAudit({
        actorUserId: row.actor_user_id,
        createdAt: row.created_at,
        from: typeof from === "number" ? from : null,
      });
    })();
    return () => { cancelled = true; };
  }, [tenantId, reloadKey]);

  return audit;
}
